import { MonitorPaygPrice } from "Hooks/Api/apiPrice";
import { useApiMonitorPayPrice } from "Hooks/ApiHooks/useApiPrice";
import { accountType } from "Models/Enums";
import CheckboxSetter from "Views/Components/Checkbox/CheckboxSetter";
import InlineSpinner from "Views/Components/Spinner/InlineSpinner";
import { MonitorCustomerDetail } from "../../GenerateCreditReport";
import { AlreadyLinkedCustomerTooltipContent, RestrictedAccessTooltipContent } from "Views/Components/Tooltip/RestrictedAccessTooltip";
import { MonitorInvoicedSummaryCard, MonitorPaygSummaryCard } from "Views/Components/PaymentSummaryCard/MonitorPaygSummaryCard";

type AddNewCustomerSegmentProps = {
	orgAccountType: accountType,
	lookupValue: string,
	businessName: string,
	monitorCustomer: MonitorCustomerDetail | null,
	isReadOnly: boolean,
	isExistingCustomer: boolean,
	updateMonitorCustomer: (value: MonitorCustomerDetail | null) => void,
}

function getTooltipContent(isReadOnly: boolean, isExistingCustomer: boolean): string | undefined {
	if (isReadOnly) return RestrictedAccessTooltipContent;
	if (isExistingCustomer) return AlreadyLinkedCustomerTooltipContent;
	return undefined;
}


export const AddNewCustomerSegment: React.FunctionComponent<AddNewCustomerSegmentProps> = ({
	orgAccountType,
	lookupValue,
	businessName,
	monitorCustomer,
	isReadOnly,
	isExistingCustomer,
	updateMonitorCustomer,
}) => {
	return (
		<>
			{orgAccountType === 'INVOICED' && (
				<AddNewInvoicedCustomerSegment
					orgAccountType={orgAccountType}
					lookupValue={lookupValue}
					businessName={businessName}
					monitorCustomer={monitorCustomer}
					isReadOnly={isReadOnly}
					isExistingCustomer={isExistingCustomer}
					updateMonitorCustomer={updateMonitorCustomer}
				/>
			)}

			{orgAccountType === 'PAYG' && (
				<AddNewPaygCustomerSegment
					orgAccountType={orgAccountType}
					lookupValue={lookupValue}
					businessName={businessName}
					monitorCustomer={monitorCustomer}
					isReadOnly={isReadOnly}
					isExistingCustomer={isExistingCustomer}
					updateMonitorCustomer={updateMonitorCustomer}
				/>
			)}
		</>
	)
}

const AddNewInvoicedCustomerSegment: React.FunctionComponent<AddNewCustomerSegmentProps> = ({
	lookupValue,
	monitorCustomer,
	isReadOnly,
	isExistingCustomer,
	updateMonitorCustomer,
}) => {

	const handleOnCheck = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
		if (!checked) {
			return updateMonitorCustomer(null);
		}
		// Update the monitor customer with the new value
		return updateMonitorCustomer({
			id: '',
			name: '',
			billingPeriod: 0,
			billingAmount: 0,
			addCustomer: checked,
		});
	}
	return (
		<>
			<MonitorInvoicedSummaryCard>
				<CheckboxSetter
					label={`Add this business ${lookupValue} as a monitored customer`}
					value={monitorCustomer?.addCustomer}
					onChecked={handleOnCheck}
					isReadOnly={isReadOnly || isExistingCustomer}
					tooltip={getTooltipContent(isReadOnly, isExistingCustomer)}
				/>
			</MonitorInvoicedSummaryCard>
		</>
	);
}

const AddNewPaygCustomerSegment: React.FunctionComponent<AddNewCustomerSegmentProps> = ({
	lookupValue,
	businessName,
	monitorCustomer,
	updateMonitorCustomer,
	isReadOnly,
	isExistingCustomer,
}) => {
	const response = useApiMonitorPayPrice();

	const handleOnCheck = (checked: boolean, data: MonitorPaygPrice) => {
		if (!checked) {
			return updateMonitorCustomer(null);
		}

		return updateMonitorCustomer({
			id: data.id,
			name: businessName,
			billingPeriod: data.billingPeriod,
			billingAmount: data.billingAmount,
			addCustomer: true,
		});
	}

	if (response.type === 'loading') return <InlineSpinner />
	if (response.type === 'error') return <div>Error: {response.error}</div>

	return (
		<MonitorPaygSummaryCard summary={response.data} showDescription={monitorCustomer?.addCustomer}>
			<CheckboxSetter
				inputProps={{ style: {} }}
				label={`Monitor customer with ABN '${lookupValue}' for ${response.data.billingPeriod} months at $${response.data.billingAmount}.`}
				value={monitorCustomer?.addCustomer}
				onChecked={(_, checked) => { handleOnCheck(checked, response.data) }}
				isReadOnly={isReadOnly || isExistingCustomer}
				tooltip={getTooltipContent(isReadOnly, isExistingCustomer)}
			/>
		</MonitorPaygSummaryCard>
	);
}